import { useEffect, useRef, useState } from "react";
import { api, type City, type Place, type SearchHit } from "../api";
import { CityMap, type MapPin } from "../CityMap";
import { money, monogram, Photo, Spinner } from "../ui";

/**
 * Витрина города: заведения вокруг гостя (п. 7 ТЗ).
 *
 * Первый экран продукта. Гость приходит сюда не за конкретным заведением, а
 * за едой рядом, поэтому список отсортирован по расстоянию, если гость дал
 * геолокацию, и по порядку, заданному платформой, если не дал.
 *
 * Закрытые заведения из списка не пропадают, а показывают, когда откроются.
 * Исчезающая карточка читается как поломка: гость видел эту кофейню утром и
 * не понимает, куда она делась.
 */

const SEARCH_DELAY_MS = 300;

/** Сценарии, по которым гость фильтрует выдачу. */
const SCENARIOS = [
  { id: "" as const, title: "Всё" },
  { id: "pickup" as const, title: "Самовывоз" },
  { id: "preorder" as const, title: "Ко времени" },
  { id: "booking" as const, title: "Столик" },
  { id: "delivery" as const, title: "Доставка" }
];

function mapPins(places: Place[]): MapPin[] {
  return places.map(place => ({
    id: place.id,
    lat: place.lat,
    lng: place.lng,
    label: monogram(place.name),
    title: place.availability.open ? place.name : `${place.name} — закрыто`,
    kind: "point"
  }));
}

/** «350 м» или «1,2 км» — гость меряет дорогу шагами, а не метрами. */
function distance(meters: number): string {
  return meters < 950 ? `${Math.round(meters / 10) * 10} м` : `${(meters / 1000).toFixed(1)} км`;
}

export function Places({
  city,
  onOpenPlace
}: {
  city: City;
  onOpenPlace: (pointId: string) => void;
}) {
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<"" | "pickup" | "preorder" | "booking" | "delivery">("");
  const [cuisine, setCuisine] = useState("");
  const [near, setNear] = useState<{ lat: number; lng: number } | null>(null);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  /*
   * Геолокация спрашивается один раз и молча.
   *
   * Отказ — обычное дело, и он ничего не ломает: без него список идёт в
   * порядке, заданном платформой, а расстояния просто не показываются.
   */
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      position => setNear({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => setNear(null),
      { timeout: 5000, maximumAge: 5 * 60_000 }
    );
  }, []);

  useEffect(() => {
    setPlaces(null);
    api
      .places(city.id, {
        accepts: scenario || undefined,
        cuisine: cuisine || undefined,
        near: near ?? undefined
      })
      .then(data => setPlaces(data.places))
      .catch((problem: Error) => setError(problem.message));
  }, [city.id, scenario, cuisine, near]);

  useEffect(() => {
    const needle = query.trim();

    // Поиск идёт по меню всех заведений города — дёргать его на каждую букву
    // нельзя.
    if (needle.length < 2) {
      setHits(null);
      return;
    }

    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      api
        .search(city.id, needle)
        .then(data => setHits(data.results))
        .catch(() => setHits([]));
    }, SEARCH_DELAY_MS);

    return () => window.clearTimeout(timer.current);
  }, [query, city.id]);

  if (error) {
    return (
      <div className="center">
        <p className="notice">{error}</p>
      </div>
    );
  }

  if (!places) return <Spinner text="Смотрим, кто рядом" />;

  const open = places.filter(place => place.availability.open);
  const cuisines = [...new Set(places.flatMap(place => place.cuisines))].sort();

  return (
    <div className="page">
      <div className="hero">
        <h1>Еда рядом</h1>
        <p>
          {open.length > 0
            ? "Заберите сами, закажите ко времени или забронируйте столик."
            : "Сейчас в городе никто не принимает заказы."}
        </p>
      </div>

      <div className="search">
        <input
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Найти блюдо в городе"
          aria-label="Поиск блюда"
          enterKeyHint="search"
        />
      </div>

      <div className="filters">
        {SCENARIOS.map(item => (
          <button
            key={item.id || "all"}
            type="button"
            className="filter-chip"
            aria-pressed={scenario === item.id}
            onClick={() => setScenario(item.id)}
          >
            {item.title}
          </button>
        ))}
      </div>

      {cuisines.length > 0 ? (
        <div className="filters">
          {cuisines.map(item => (
            <button
              key={item}
              type="button"
              className="filter-chip"
              aria-pressed={cuisine === item}
              onClick={() => setCuisine(current => (current === item ? "" : item))}
            >
              {item}
            </button>
          ))}
        </div>
      ) : null}

      {/*
        Карта показывается по кнопке, а не сразу.

        Список отвечает на «что есть рядом» быстрее карты и читается на ходу;
        карту открывают, когда решают, далеко ли идти.
      */}
      <div className="map-toggle">
        <button type="button" className="btn ghost" onClick={() => setMapOpen(value => !value)}>
          {mapOpen ? "Скрыть карту" : "Показать на карте"}
        </button>
      </div>

      {mapOpen ? (
        <CityMap
          center={near ?? { lat: city.lat, lng: city.lng }}
          you={near}
          pins={mapPins(places)}
          onPick={onOpenPlace}
        />
      ) : null}

      {hits ? (
        <section>
          <div className="section-head">
            <h2>Нашлось</h2>
          </div>
          {hits.length === 0 ? (
            <p className="empty">Ничего похожего. Попробуйте другое слово.</p>
          ) : (
            <div className="venues" style={{ marginTop: 12 }}>
              {hits.map(hit => (
                <button
                  key={hit.productId}
                  type="button"
                  className="hit"
                  onClick={() => onOpenPlace(hit.pointId)}
                >
                  <span className="hit-title">
                    {hit.title}
                    <br />
                    <span className="hit-where">{hit.pointName}</span>
                  </span>
                  <span className="price">{money(hit.price)}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      ) : (
        <section>
          <div className="section-head">
            <h2>Заведения</h2>
          </div>

          <div className="venues" style={{ marginTop: 12 }}>
            {places.map((place, index) => (
              <Venue key={place.id} place={place} index={index} onOpen={onOpenPlace} />
            ))}
            {places.length === 0 ? (
              <p className="empty">
                {scenario || cuisine
                  ? "По этому фильтру пока пусто."
                  : "В этом городе ещё нет заведений."}
              </p>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}

function Venue({
  place,
  index,
  onOpen
}: {
  place: Place;
  index: number;
  onOpen: (pointId: string) => void;
}) {
  // Через переменную, а не через `!place.availability.open`: разбор варианта
  // должен быть виден типам, иначе причина закрытия «не существует».
  const availability = place.availability;
  const closed = !availability.open;

  return (
    <button
      type="button"
      className={`venue${closed ? " closed" : ""}`}
      // Список появляется волной сверху вниз: задержка растёт с позицией и
      // упирается в потолок, чтобы десятое заведение не ждало секунду.
      style={{ animationDelay: `${Math.min(index * 45, 360)}ms` }}
      onClick={() => !closed && onOpen(place.id)}
      disabled={closed}
      aria-label={place.name}
    >
      <Photo src={place.logoUrl} title={place.name} className="venue-logo" />

      <span className="venue-body">
        <span className="venue-name">{place.name}</span>
        <span className="venue-note">
          {availability.open ? place.address || place.description : availability.message}
        </span>
      </span>

      {closed ? (
        <span className="badge">Закрыто</span>
      ) : (
        <span className="venue-eta">
          {place.distanceM !== null ? distance(place.distanceM) : `~${place.avgPrepMinutes} мин`}
        </span>
      )}
    </button>
  );
}
